"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { Timeline, TimelineContent, TimelineHeader, TimelineIndicator, TimelineItem, TimelineSeparator } from "@/components/reui/timeline";
import { Section } from "@/components/section";
import { Bar, BarChart, XAxis, LabelList, Rectangle } from "recharts";
import { ChartContainer, type ChartConfig, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import { Icon } from "@/components/common/icon";
import { getSystemIcon } from "@/lib/system-lucide-icon-map";
import { hasData } from "@/helpers/has-data";
const chartConfig = {
  minSalary: {
    label: "Min Salary",
    color: "var(--primary)"
  },
  maxSalary: {
    label: "Max Salary",
    color: "var(--primary)"
  }
} satisfies ChartConfig;
const CustomXAxisTick = props => {
  const $ = _c(5);
  const {
    x,
    y,
    payload
  } = props;
  const t0 = `translate(${x},${y})`;
  let t1;
  if ($[0] !== payload.value) {
    t1 = <text x={0} y={0} dy={16} textAnchor="middle" fill="currentColor" className="text-muted-foreground/80 fill-muted-foreground/80 text-[10px] font-semibold sm:text-[11px]">{payload.value}</text>;
    $[0] = payload.value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = <g transform={t0}>{t1}</g>;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
type CareerPathAndGrowthProps = {
  section: PageSections;
  data: {
    seoHeading: string;
    heading: string;
    description: string;
    graphHeading: string;
    graphRecord: ChartData[];
    dataSource: string;
    cards: TimelineCard[];
    serviceId: 39;
    createdAt: string;
    updatedAt: string;
    countryCode: string;
    hasGrouped: boolean;
  };
};
type TimelineCard = {
  icon: string;
  heading: string;
  subHeading: string;
  description: string;
};
type ChartData = {
  role: string;
  salary: number;
  minSalary: number;
  maxSalary: number;
  displayMinSalary: string;
  displayMaxSalary: string;
};
const CareerPathAndGrowth = t0 => {
  const $ = _c(47);
  const {
    section,
    data
  } = t0;
  const [activeStep, setActiveStep] = useState(2);
  if (!hasData(data)) {
    return null;
  }
  const chartData = data?.graphRecord;
  let t1;
  if ($[0] !== data.hasGrouped || $[1] !== section.sectionClass) {
    t1 = cn("from-primary/5 to-secondary/5 dark:from-background dark:to-background bg-linear-to-b", section.sectionClass, {
      "rounded-3xl py-4! sm:py-6!": data.hasGrouped
    });
    $[0] = data.hasGrouped;
    $[1] = section.sectionClass;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== activeStep || $[4] !== data.cards) {
    t2 = data?.cards?.map((item, index) => {
      const isActive = index === activeStep;
      return <TimelineItem key={`job-growth-${index}`} step={index} className="relative mt-0! flex-1 cursor-pointer pe-0! pt-20 text-center select-none group-data-[orientation=horizontal]/timeline:mt-0 group-data-[orientation=horizontal]/timeline:not-last:pe-0" onClick={() => setActiveStep(index)}>{isActive && <div className="bg-primary absolute top-14 left-1/2 z-0 h-10 w-0.5 -translate-x-1/2 transition-all duration-300" />}<TimelineHeader><TimelineSeparator className="bg-primary/20 absolute top-7 left-1/2 z-0 h-0.5 w-[calc(100%+1.5rem)] group-last/timeline-item:hidden group-data-[orientation=horizontal]/timeline:top-7! group-data-[orientation=horizontal]/timeline:left-1/2! group-data-[orientation=horizontal]/timeline:w-[calc(100%+1.5rem)]! group-data-[orientation=horizontal]/timeline:translate-x-0! group-data-[orientation=horizontal]/timeline:translate-y-0!" /><TimelineIndicator className={cn("dark:bg-card absolute top-0 left-1/2 z-10 flex size-14 -translate-x-1/2 items-center justify-center rounded-full border-2 bg-white transition-all duration-300", "group-data-[orientation=horizontal]/timeline:top-0! group-data-[orientation=horizontal]/timeline:left-1/2! group-data-[orientation=horizontal]/timeline:-translate-x-1/2! group-data-[orientation=horizontal]/timeline:translate-y-0!", isActive ? "border-primary text-primary scale-110 shadow-md" : "border-primary/20 text-primary/60 hover:border-primary/50 hover:text-primary")}><Icon name={getSystemIcon(item.icon)} className="text-primary" /></TimelineIndicator></TimelineHeader><TimelineContent className="mt-3 flex h-full flex-col items-center text-center"><div className={cn("flex h-full w-full flex-col items-center justify-start rounded-2xl border-2 p-4 text-center transition-all duration-300", isActive ? "bg-primary/5 border-primary shadow-xs" : "border-border hover:bg-primary/5 hover:border-primary/20 bg-transparent")}><div className={cn("mb-1 text-sm leading-snug font-bold transition-colors duration-300 sm:text-base", isActive ? "text-primary" : "text-foreground")}>{item.heading}</div><div className="text-primary mb-2 text-xs font-semibold sm:text-sm">{item.subHeading}</div><p className="text-muted-foreground mx-auto max-w-40 text-xs leading-relaxed">{item.description}</p></div></TimelineContent></TimelineItem>;
    });
    $[3] = activeStep;
    $[4] = data.cards;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== activeStep || $[7] !== t2) {
    t3 = <div className="overflow-x-auto pb-4"><Timeline value={activeStep} onValueChange={setActiveStep} orientation="horizontal" className="relative mt-4 flex w-full min-w-225 justify-between gap-6">{t2}</Timeline></div>;
    $[6] = activeStep;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== data.graphHeading) {
    t4 = <h4 className="text-primary dark:text-primary-foreground pb-6 text-center text-base font-bold sm:text-lg">{data.graphHeading}</h4>;
    $[9] = data.graphHeading;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      top: 25,
      right: 10,
      left: 10,
      bottom: 20
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <CustomXAxisTick />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <XAxis dataKey="role" tick={t6} tickLine={false} axisLine={{
      stroke: "currentColor",
      className: "text-border"
    }} interval={0} />;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ChartTooltip cursor={false} content={<ChartTooltipContent formatter={_temp} />} />;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [8, 8, 0, 0];
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== activeStep) {
    t10 = props => {
      const {
        index: index_0
      } = props;
      const isHighlighted = index_0 === activeStep;
      return <Rectangle {...props} className={cn("transition-all duration-300 ease-in-out", isHighlighted ? "fill-primary/75" : "fill-secondary/25")} />;
    };
    $[16] = activeStep;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <LabelList dataKey="displayMinSalary" position="top" offset={10} className="fill-foreground text-[10px] font-bold" />;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10) {
    t12 = <Bar dataKey="minSalary" radius={t9} barSize={40} isAnimationActive={true} shape={t10}>{t11}</Bar>;
    $[19] = t10;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = [8, 8, 0, 0];
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== activeStep) {
    t14 = props_0 => {
      const {
        index: index_1
      } = props_0;
      const isHighlighted_0 = index_1 === activeStep;
      return <Rectangle {...props_0} className={cn("transition-all duration-300 ease-in-out", isHighlighted_0 ? "fill-primary" : "fill-secondary/50")} />;
    };
    $[22] = activeStep;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <LabelList dataKey="displayMaxSalary" position="top" offset={10} className="fill-foreground text-[10px] font-bold" />;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t14) {
    t16 = <Bar dataKey="maxSalary" radius={t13} barSize={40} isAnimationActive={true} shape={t14}>{t15}</Bar>;
    $[25] = t14;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== chartData || $[28] !== t12 || $[29] !== t16) {
    t17 = <div className="w-full scrollbar-thin overflow-x-auto pb-4"><div className="w-full min-w-150"><ChartContainer config={chartConfig} className="h-75 w-full"><BarChart accessibilityLayer={true} data={chartData} margin={t5}>{t7}{t8}{t12}{t16}</BarChart></ChartContainer></div></div>;
    $[27] = chartData;
    $[28] = t12;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== data.dataSource) {
    t18 = <div className="text-muted-foreground/80 mt-4 text-center text-xs">Source: {data.dataSource}</div>;
    $[31] = data.dataSource;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t17 || $[34] !== t18 || $[35] !== t4) {
    t19 = <div className="mt-6 pb-4">{t4}{t17}{t18}</div>;
    $[33] = t17;
    $[34] = t18;
    $[35] = t4;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t19 || $[38] !== t3) {
    t20 = <div className="flex w-full flex-col items-stretch gap-8"><div className="dark:bg-card border-border flex flex-col rounded-3xl border bg-white p-6 shadow-xs sm:p-8">{t3}{t19}</div></div>;
    $[37] = t19;
    $[38] = t3;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== data.description || $[41] !== data.heading || $[42] !== data.seoHeading || $[43] !== section.sectionId || $[44] !== t1 || $[45] !== t20) {
    t21 = <Section className={t1} title={data.heading} seoTitle={data.seoHeading} paragraph={data.description} id={section.sectionId}>{t20}</Section>;
    $[40] = data.description;
    $[41] = data.heading;
    $[42] = data.seoHeading;
    $[43] = section.sectionId;
    $[44] = t1;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  return t21;
};
export default CareerPathAndGrowth;
function _temp(value, name, item_0) {
  const isMin = name === "minSalary" || name === "Min Salary";
  const displayVal = isMin ? item_0.payload.displayMinSalary : item_0.payload.displayMaxSalary;
  const labelText = isMin ? "Min Salary" : "Max Salary";
  return <div className="flex items-center gap-2"><div className={cn("size-2 shrink-0 rounded-xs", isMin ? "bg-primary" : "bg-primary/60")} /><span className="text-muted-foreground">{labelText}:</span><span className="text-foreground font-mono font-bold">{displayVal}</span></div>;
}